// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: whether the server wants to ask "Am I getting in your way?" (`ask_change` on GET
// /konvo/corner/pending, GET /konvo/corner/list and an outcome's reply). Kept apart from the stores
// that read it (Corner, ReadyList) and the one that draws it (CornerAskChange), so none of them
// import each other. A reply for a request sent before the person answered here does not count.

import {makeAutoObservable} from 'mobx';

export class AskChangeSignalStore {
	/** The server wants to ask. */
	pending = false;
	/** How many pop-ups in a row were closed (the number the card names). */
	closed = 0;
	/** When the person last answered (or undid) here: older replies are stale. */
	private changedAt = 0;

	constructor() {
		makeAutoObservable<this, 'changedAt'>(this, {changedAt: false}, {autoBind: true});
	}

	/**
	 * What a reply said. `requestedAt` is when its request went out; absent fields (an older server)
	 * change nothing.
	 */
	report(body: unknown, requestedAt: number): void {
		if (!body || typeof body !== 'object') return;
		const record = body as {ask_change?: unknown; ask_change_closed?: unknown};
		if (typeof record.ask_change !== 'boolean') return;
		if (requestedAt < this.changedAt) return;
		this.pending = record.ask_change;
		if (typeof record.ask_change_closed === 'number' && Number.isFinite(record.ask_change_closed)) {
			this.closed = Math.max(0, Math.round(record.ask_change_closed));
		}
	}

	/** Answered or undone here: set it now, and drop replies already on their way. */
	set(pending: boolean, now = Date.now()): void {
		this.changedAt = now;
		this.pending = pending;
	}

	reset(): void {
		this.pending = false;
		this.closed = 0;
		this.changedAt = 0;
	}
}

export default new AskChangeSignalStore();
